// ========================
// Global export constants
// ========================
// ========================
// Source Platform Colors Helper
// ========================
// Import the existing source colors from constants
import {
  DEFAULT_KWELLO_COLOR,
  DEFAULT_KWELLO_SECONDARY_COLOR,
  SOURCE_BLOGS_COLOR,
  SOURCE_FACEBOOK_COLOR,
  SOURCE_FORUMS_COLOR,
  SOURCE_INSTAGRAM_COLOR,
  SOURCE_NEWS_COLOR,
  SOURCE_QQ_COLOR,
  SOURCE_REDDIT_COLOR,
  SOURCE_TUMBLR_COLOR,
  SOURCE_TWITTER_COLOR,
  SOURCE_YOUTUBE_COLOR,
} from 'constants/index';

// Import UI chart colors for consistency between app and PPT exports
import { CHART_COLORS as UI_CHART_COLORS } from 'common/Charts/Charts.const';

export const POS_TITLE_X = 0.5;
export const POS_TITLE_Y = 0.63;
export const POS_SUBTITLE_X = 0.5;
export const POS_SUBTITLE_Y = 1;

export const POS_PAGE_CONTENT_X = 0.5;
export const POS_PAGE_CONTENT_Y = 1.5;

export const MARGIN_LEFT = 0.5;

export const COLOR_PRIMARY = '03386F';
export const COLOR_SECONDARY = '595959';
export const COLOR_TERTIARY = '000000';

// Define Conflict color export constants for UI parity
export const COLOR_CONFLICT_WARNING = 'FFFAE6'; // Light yellow for conflict rows (matches UI)
export const COLOR_BG = 'FFFFFF';
export const COLOR_BORDER = '473A2E';
export const COLOR_BODY = '473A2E';

// ========================
// Slide Background Colors
// ========================
export const COLOR_SLIDE_BG = 'F7F9FB'; // Light gray background for content slides

// ========================
// Header Colors
// ========================
export const COLOR_HEADER_PROJECT = 'FFFFFF'; // White for project name
export const COLOR_HEADER_SEPARATOR = '4F667F'; // Blue-gray for pipe separator
export const COLOR_HEADER_TITLE = 'CBDBEC'; // Light blue for page title

// ========================
// Cover Slide Colors
// ========================
export const COLOR_COVER_TITLE = 'FFFFFF'; // White for main title
export const COLOR_COVER_SUBTITLE = '8399AF'; // Light blue-gray for subtitle and date

// ========================
// Feed Card Colors
// ========================
export const COLOR_FEED_CARD_BG = 'FFFFFF'; // White background for cards
export const COLOR_FEED_CARD_BORDER = 'EEF1F6'; // Light border with transparency
export const COLOR_FEED_PROFILE_NAME = '283242'; // Dark blue-gray for profile/author names
export const COLOR_FEED_TYPE = 'B6BABE'; // Light gray for type (Earned/Owned)
export const COLOR_FEED_TITLE = '4F5868'; // Medium gray for title text
export const COLOR_FEED_SNIPPET = '4F5868'; // Same as title (but not bold)
export const COLOR_FEED_META = '808080'; // Medium gray for metadata
export const COLOR_FEED_VIEW_BG = 'F0F1F4'; // Light gray for View button background
export const COLOR_FEED_VIEW_TEXT = '3D454E'; // Dark gray for View button text

// ========================
// Profile Slide Colors
// ========================
export const COLOR_PROFILE_BIO = '4F5868'; // Medium gray for bio text
export const COLOR_PROFILE_LABELS = 'B6BABE'; // Light gray for labels (Country, Social Rank, KPI names)
export const COLOR_PROFILE_CHART_TITLE = '283242'; // Dark blue-gray for chart titles

// ========================
// Metric/KPI Colors
// ========================
// Only Earned and Owned are colored to match Classic app conventions
// All other KPIs use default dark color
export const COLOR_METRIC_EARNED = DEFAULT_KWELLO_COLOR.replace('#', ''); // f36633 - Orange
export const COLOR_METRIC_OWNED = DEFAULT_KWELLO_SECONDARY_COLOR.replace('#', ''); // 73b761 - Green
export const COLOR_METRIC_DEFAULT = '1F2937'; // Dark gray for all other metrics

// ========================
// Feed Card Text Settings
// ========================

// Base font size for the main content text in feed cards
export const FEED_TEXT_SIZE = 10;

// Font size for profile names displayed in feed cards
export const FEED_PROFILE_NAME_SIZE = 9;

// Font size for footer metadata (date, reach, sentiment, source)
export const FEED_FOOTER_TEXT_SIZE = 8;

// Color for footer text - light gray for de-emphasis
export const FEED_FOOTER_TEXT_COLOR = 'AAA0A5';

// Maximum character length for feed content text
// Increased from 300 to 1000 for testing auto-shrink feature (Ticket #21764)
// PowerPoint will auto-shrink text using fit: 'shrink' to prevent overflow
export const FEED_MAX_LENGTH = 1000;

// Maximum number of lines to display in feed cards
// Increased from 5 to 10 to accommodate longer posts (Ticket #21764)
// Text will auto-shrink if it exceeds the available space
export const FEED_TWEET_MAX_LINES = 10;

export const AVATAR_SIZE = 0.5;

export const FONT_SIZE_H1 = 28;
export const FONT_SIZE_H2 = 24;
export const FONT_SIZE_H3 = 20;
export const FONT_SIZE_P = 8;
export const FONT_SIZE_TABLE_VALUES = 10;
export const TEXTBOX_HEIGHT = 0.5;
export const POS_SLIDE_NUMBER_X = 0.175;
export const POS_SLIDE_NUMBER_Y = '93.82%';
export const COLOR_SLIDE_NUMBER = COLOR_SECONDARY;
export const FONT_SIZE_SLIDE_NUMBER = 12;

// Define the position and sizing
export const POS_LOCATION_LABEL_X = POS_SUBTITLE_X; // Start at left margin
export const POS_LOCATION_VALUE_X = 1.8; // Place value slightly to the right
export const POS_LOCATION_Y = POS_SUBTITLE_Y; // Align with subtitle
export const SIZE_LOCATION_LABEL_W = 1.3; // Label width
export const SIZE_LOCATION_VALUE_W = 10.9; // Wider for actual location text

export const RECT_RADIUS = 0.1;

export const SIZE_TABLE_W = 12.3;
export const COLOR_TABLE_HEADER = 'F2F2F2';
export const COLOR_TABLE_BORDER = 'BBBBBB';
export const SIZE_TABLE_BORDER = '0.5';
export const SPACING_TABLE_MARGIN = 0.05;

// Column width ratios for tables
export const COL_WIDTH_ACTIVITIES = [
  '10%',
  '30%',
  '15%',
  '15%',
  '15%',
  '10%',
  '10%',
  '5%',
  '5%',
  '5%',
];
export const COL_WIDTH_OBJECTIVES = ['30%', '70%'];
export const PPT_LAYOUT = 'LAYOUT_WIDE';

export const HEADER_TEXT_STYLE = {
  x: 0.2,
  y: 0, // Overlay on header background like NextGen
  fontSize: FONT_SIZE_H2,
  color: COLOR_BG, // White text
  bold: false,
  h: 0.8, // Match adjusted header height to preserve aspect ratio
  valign: 'middle',
};

export const FOOTER_DISCLAIMER = 'Disclaimer: ';
export const FOOTER_TEXT =
  'The data within this export is correct at the time of data acquisition from publicly available sources and can be removed or changed at source which may not be reflected in this export.';

export const FOOTER_TEXT_STYLE = {
  x: 0.7,
  y: '94%',
  fontSize: FONT_SIZE_P,
  color: COLOR_TERTIARY,
  bold: false,
  h: TEXTBOX_HEIGHT,
};

export const CONTENT_START_X = 0.5; // Aligned with chart positioning for consistency

export const CONTENT_START_Y = 1.8;

// ========================
// Chart Export Constants
// ========================
// Use UI chart colors for consistency (stripped of # prefix for pptxgenjs)
// Exclude colors that don't render well in PPT: yellow (#ECC846), light mint (#95DABB), gray (#C4C4C4)
const EXCLUDED_COLORS = ['#ECC846', '#95DABB', '#C4C4C4'];
export const CHART_COLORS = UI_CHART_COLORS.filter(color => !EXCLUDED_COLORS.includes(color)).map(
  color => color.replace('#', ''),
);

export const CHART_DEFAULT_OPTIONS = {
  x: 1,
  y: 1.5,
  w: 11,
  h: 4,
  showLegend: true,
  legendPos: 'r',
  showTitle: false,
  dataLabelPosition: 'bestFit',
  dataLabelFontSize: 10,
  showValue: true,
};

export const BAR_CHART_OPTIONS = {
  ...CHART_DEFAULT_OPTIONS,
  barDir: 'bar',
  dataLabelPosition: 'outEnd',
};

export const PIE_CHART_OPTIONS = {
  ...CHART_DEFAULT_OPTIONS,
  dataLabelPosition: 'outside',
  showDataLabels: true,
};

export const DOUGHNUT_CHART_OPTIONS = {
  ...CHART_DEFAULT_OPTIONS,
  holeSize: 50,
};

export const LINE_CHART_OPTS = {
  x: CONTENT_START_X,
  y: CONTENT_START_Y,
  w: '95%',
  h: 5,
  plotArea: { fill: { color: COLOR_BG } },
  showLegend: true,
  legendPos: 'b',
  showTitle: true,
  lineDataSymbol: 'none',
  titleColor: COLOR_SECONDARY,
  titleFontSize: FONT_SIZE_H2 / 2,
  legendColor: COLOR_SECONDARY,
  legendFontSize: FONT_SIZE_P,
};

// Convert hex colors to PPT format (remove # if present)
const formatColorForPPT = color => color.replace('#', '');

// Map source names to their colors for PPT usage
export const SOURCE_COLORS_PPT = {
  Twitter: formatColorForPPT(SOURCE_TWITTER_COLOR),
  News: formatColorForPPT(SOURCE_NEWS_COLOR),
  YouTube: formatColorForPPT(SOURCE_YOUTUBE_COLOR),
  Youtube: formatColorForPPT(SOURCE_YOUTUBE_COLOR), // API returns both 'YouTube' and 'Youtube' - handle both cases
  Blogs: formatColorForPPT(SOURCE_BLOGS_COLOR),
  Reddit: formatColorForPPT(SOURCE_REDDIT_COLOR),
  Tumblr: formatColorForPPT(SOURCE_TUMBLR_COLOR),
  Facebook: formatColorForPPT(SOURCE_FACEBOOK_COLOR),
  Instagram: formatColorForPPT(SOURCE_INSTAGRAM_COLOR),
  Forums: formatColorForPPT(SOURCE_FORUMS_COLOR),
  QQ: formatColorForPPT(SOURCE_QQ_COLOR),
  Default: '999999',
};

// Get color for a source platform
export const getSourceColorForPPT = source => {
  return SOURCE_COLORS_PPT[source] || SOURCE_COLORS_PPT.Default;
};
